import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Download, 
  FileText, 
  CheckCircle, 
  AlertTriangle,
  Loader2,
  Package,
  File,
  Code,
  Table,
  ArrowRight,
  Sparkles,
  Eye
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { useWizardAutoSave } from '../utils/useWizardAutoSave';

interface ExportWizardProps {
  isOpen: boolean;
  companyId: string;
  accessToken: string;
  onClose: () => void;
}

interface ExportStep {
  id: 'select' | 'configure' | 'preview' | 'download';
  title: string;
  description: string;
}

const STEPS: ExportStep[] = [
  {
    id: 'select',
    title: 'Select Platform',
    description: 'Choose your target ITSM platform'
  },
  {
    id: 'configure',
    title: 'Configure Export',
    description: 'Select items and options'
  },
  {
    id: 'preview',
    title: 'Preview',
    description: 'Review generated files'
  },
  {
    id: 'download',
    title: 'Download',
    description: 'Get your export package'
  }
];

export function ExportWizard({
  isOpen,
  companyId,
  accessToken,
  onClose
}: ExportWizardProps) {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [vendor, setVendor] = useState<string>('jira');
  const [format, setFormat] = useState<string>('json');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [includeGuide, setIncludeGuide] = useState(true);
  const [acceptedOnly, setAcceptedOnly] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exportResult, setExportResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Auto-save (the wizard rule in CLAUDE.md): the choices survive a refresh; the download
  // step is not restored because its result is not stored. Saved on Next and Back, five
  // seconds after a change, and dropped once the export has been generated.
  const { saveDraft, loadDraft, deleteDraft } = useWizardAutoSave({
    draftKey: 'wizard_export',
    entityId: companyId,
    accessToken,
    getData: () => ({ step: Math.min(currentStep, STEPS.length - 2), vendor, format, includeGuide, acceptedOnly }),
  });
  const restored = useRef(false);
  useEffect(() => {
    if (!isOpen || restored.current) return;
    restored.current = true;
    loadDraft().then((draft) => {
      if (!draft || typeof draft !== 'object') return;
      if (typeof draft.vendor === 'string') setVendor(draft.vendor);
      if (typeof draft.format === 'string') setFormat(draft.format);
      if (typeof draft.includeGuide === 'boolean') setIncludeGuide(draft.includeGuide);
      if (typeof draft.acceptedOnly === 'boolean') setAcceptedOnly(draft.acceptedOnly);
      if (typeof draft.step === 'number') setCurrentStep(Math.max(0, Math.min(draft.step, STEPS.length - 2)));
    }).catch(() => undefined);
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen || !restored.current || exportResult) return;
    const timer = setTimeout(() => { saveDraft(false); }, 5000);
    return () => clearTimeout(timer);
  }, [isOpen, vendor, format, includeGuide, acceptedOnly, currentStep, exportResult]);

  const vendors = [
    { id: 'jira', name: 'Jira / JSM', color: '#3B82F6', formats: ['json', 'xml'] },
    { id: 'servicenow', name: 'ServiceNow', color: '#6366F1', formats: ['json', 'csv'] },
    { id: 'zendesk', name: 'Zendesk', color: '#22C55E', formats: ['json'] },
    { id: 'ivanti-neurons', name: 'Ivanti Neurons', color: '#A855F7', formats: ['json'] },
    { id: 'bmc-helix', name: 'BMC Helix', color: '#F97316', formats: ['json', 'xml'] },
    { id: 'freshservice', name: 'Freshservice', color: '#F59E0B', formats: ['json'] }
  ];

  const selectedVendor = vendors.find(v => v.id === vendor);

  const handleNext = async () => {
    if (currentStep === STEPS.length - 2) {
      // Generate export before moving to download step
      await generateExport();
    } else {
      await saveDraft(false);
    }
    setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = async () => {
    await saveDraft(false);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const generateExport = async () => {
    setGenerating(true);
    setError(null);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/export/generate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            companyId,
            configItemIds: selectedItems.length > 0 ? selectedItems : undefined,
            vendor,
            format,
            includeImplementationGuide: includeGuide,
            acceptedMappingsOnly: acceptedOnly
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setExportResult(data.export);
        // The export exists now: the draft has done its job.
        deleteDraft().catch(() => undefined);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate export');
      }
    } catch (error) {
      console.error('Export generation error:', error);
      setError('Failed to generate export');
    } finally {
      setGenerating(false);
    }
  };

  const downloadFile = async (fileName: string) => {
    if (!exportResult) return;

    const file = exportResult.files.find((f: any) => f.fileName === fileName);
    if (!file) return;

    // Create blob and download
    const blob = new Blob([file.content], { type: getFileType(file.fileType) });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllAsZip = async () => {
    if (!exportResult?.downloadUrl) return;

    window.open(exportResult.downloadUrl, '_blank');
  };

  const getFileType = (fileType: string) => {
    const types: Record<string, string> = {
      'json': 'application/json',
      'xml': 'application/xml',
      'csv': 'text/csv',
      'yaml': 'application/x-yaml'
    };
    return types[fileType] || 'text/plain';
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'json':
        return <Code className="h-5 w-5 text-blue-600" />;
      case 'xml':
        return <Code className="h-5 w-5 text-purple-600" />;
      case 'csv':
        return <Table className="h-5 w-5 text-green-600" />;
      default:
        return <File className="h-5 w-5" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Configuration
          </DialogTitle>
          <DialogDescription>
            Generate vendor-ready import files and implementation guides
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    ${index <= currentStep ? 'bg-primary text-primary-foreground' : 'bg-muted'}
                  `}
                >
                  {index < currentStep ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <p className="text-xs mt-1 text-center">{step.title}</p>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 ${index < currentStep ? 'bg-primary' : 'bg-muted'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step Content */}
        <div className="min-h-[400px]">
          {/* Step 1: Select Platform */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold">Select Target Platform</h3>
              <div className="grid grid-cols-2 gap-3">
                {vendors.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setVendor(v.id)}
                    className={`
                      p-4 border rounded-lg text-left transition-all
                      ${vendor === v.id ? 'border-primary bg-primary/5' : 'hover:border-primary/50'}
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: v.color }} />
                      <div>
                        <p className="font-medium">{v.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {v.formats.join(', ').toUpperCase()}
                        </p>
                      </div>
                    </div>
                    {vendor === v.id && (
                      <CheckCircle className="h-5 w-5 text-primary absolute top-2 right-2" />
                    )}
                  </button>
                ))}
              </div>

              {selectedVendor && (
                <div>
                  <Label>Export Format</Label>
                  <div className="flex gap-2 mt-2">
                    {selectedVendor.formats.map((fmt) => (
                      <Button
                        key={fmt}
                        variant={format === fmt ? 'default' : 'outline'}
                        onClick={() => setFormat(fmt)}
                      >
                        {fmt.toUpperCase()}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Configure Export */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold">Export Options</h3>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-guide"
                    checked={includeGuide}
                    onCheckedChange={(checked) => setIncludeGuide(checked as boolean)}
                  />
                  <Label htmlFor="include-guide" className="cursor-pointer">
                    Include implementation guide
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="accepted-only"
                    checked={acceptedOnly}
                    onCheckedChange={(checked) => setAcceptedOnly(checked as boolean)}
                  />
                  <Label htmlFor="accepted-only" className="cursor-pointer">
                    Use accepted AI mappings only
                  </Label>
                </div>
              </div>

              <Alert>
                <Sparkles className="h-4 w-4" />
                <AlertDescription>
                  <strong>Smart Export:</strong> We'll automatically include all configuration items
                  that have been filled out. You can also manually select specific items below.
                </AlertDescription>
              </Alert>

              <div>
                <Label>Export Scope</Label>
                <div className="mt-2 space-y-2">
                  <Button variant="outline" className="w-full justify-start">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    All configured items (recommended)
                  </Button>
                  <p className="text-xs text-muted-foreground px-4">
                    We'll include all items that have configuration details, notes, or accepted mappings
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {currentStep === 2 && (
            <div className="space-y-4">
              {generating ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                  <p className="text-lg font-medium">Generating export...</p>
                  <p className="text-sm text-muted-foreground">
                    This may take a moment while we create your files
                  </p>
                </div>
              ) : exportResult ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Export Preview</h3>
                    <Badge variant="secondary">
                      {exportResult.files.length} file(s)
                    </Badge>
                  </div>

                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="border rounded-lg p-3">
                      <div className="text-2xl font-bold">{exportResult.summary.totalItems}</div>
                      <div className="text-xs text-muted-foreground">Total Items</div>
                    </div>
                    <div className="border rounded-lg p-3">
                      <div className="text-2xl font-bold text-green-600">{exportResult.summary.exportedItems}</div>
                      <div className="text-xs text-muted-foreground">Automated</div>
                    </div>
                    <div className="border rounded-lg p-3">
                      <div className="text-2xl font-bold text-yellow-600">{exportResult.summary.manualItems}</div>
                      <div className="text-xs text-muted-foreground">Manual Steps</div>
                    </div>
                  </div>

                  {/* Files List */}
                  <div>
                    <Label>Generated Files</Label>
                    <div className="mt-2 space-y-2">
                      {exportResult.files.map((file: any) => (
                        <div key={file.fileName} className="border rounded-lg p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              {getFileIcon(file.fileType)}
                              <div>
                                <p className="font-medium">{file.fileName}</p>
                                <p className="text-sm text-muted-foreground">{file.purpose}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {(file.size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadFile(file.fileName)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Preview
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Implementation Guide */}
                  {exportResult.implementationGuide && (
                    <div>
                      <Label>Implementation Guide</Label>
                      <div className="mt-2 border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-medium">Step-by-step instructions</p>
                            <p className="text-sm text-muted-foreground">
                              Estimated time: {exportResult.implementationGuide.estimatedTime}
                            </p>
                          </div>
                          <Badge>
                            {exportResult.implementationGuide.sections.length} sections
                          </Badge>
                        </div>

                        {exportResult.implementationGuide.prerequisites.length > 0 && (
                          <Alert>
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                              <strong>Prerequisites:</strong>
                              <ul className="list-disc list-inside mt-1 text-sm">
                                {exportResult.implementationGuide.prerequisites.map((prereq: string, idx: number) => (
                                  <li key={idx}>{prereq}</li>
                                ))}
                              </ul>
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-yellow-600" />
                  <p>Failed to generate export</p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Download */}
          {currentStep === 3 && exportResult && (
            <div className="space-y-4">
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-semibold mb-2">Export Ready</h3>
                <p className="text-muted-foreground mb-6">
                  Your {vendor} export package has been generated
                </p>

                <Button onClick={downloadAllAsZip} size="lg" className="mb-4">
                  <Package className="h-5 w-5 mr-2" />
                  Download Complete Package (.zip)
                </Button>

                <p className="text-sm text-muted-foreground">
                  Or download individual files below
                </p>
              </div>

              <div className="space-y-2">
                {exportResult.files.map((file: any) => (
                  <div key={file.fileName} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {getFileIcon(file.fileType)}
                      <div>
                        <p className="font-medium">{file.fileName}</p>
                        <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadFile(file.fileName)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-2 justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={currentStep === 0 ? onClose : handleBack}
            disabled={generating}
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>

          <div className="flex gap-2">
            {currentStep < STEPS.length - 1 && (
              <Button
                onClick={handleNext}
                disabled={generating || (currentStep === 2 && !exportResult)}
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : currentStep === 1 ? (
                  <>
                    Generate Export
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            )}

            {currentStep === STEPS.length - 1 && (
              <Button onClick={onClose}>
                Done
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}